import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyNavigationEffectValue,
  resetNavigationEffectValue,
} from "../shared/page-navigation-effects.ts";

const attribute = "data-migrosone-a11y-highlight-headings";
const styleVariable = "--migrosone-a11y-heading-emphasis-style";
const headingVariables = [
  styleVariable,
  "--migrosone-a11y-heading-outline",
  "--migrosone-a11y-heading-outline-offset",
  "--migrosone-a11y-heading-background",
  "--migrosone-a11y-heading-box-shadow",
] as const;
const allowedStyles = ["outline", "background", "left-border"] as const;

type HeadingStyle = (typeof allowedStyles)[number];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readBooleanDefault(config: unknown) {
  if (!isRecord(config)) {
    return false;
  }

  if (typeof config.enabledByDefault === "boolean") {
    return config.enabledByDefault;
  }

  return typeof config.defaultEnabled === "boolean" ? config.defaultEnabled : false;
}

function readStyle(config: unknown): HeadingStyle {
  const configuredStyle =
    isRecord(config) && typeof config.style === "string"
      ? config.style.trim()
      : "outline";

  return allowedStyles.includes(configuredStyle as HeadingStyle)
    ? (configuredStyle as HeadingStyle)
    : "outline";
}

function resolveEnabled(config: unknown, preference: unknown) {
  return typeof preference === "boolean" ? preference : readBooleanDefault(config);
}

function resetHeadingVariables(document: Document) {
  for (const variable of headingVariables) {
    resetNavigationEffectValue({ attribute, document, variable });
  }
}

function applyHeadingStyle(document: Document, style: HeadingStyle) {
  applyNavigationEffectValue({
    attribute,
    document,
    value: style,
    variable: styleVariable,
  });

  if (style === "background") {
    applyNavigationEffectValue({
      attribute,
      document,
      value: "rgba(247, 201, 72, 0.28)",
      variable: "--migrosone-a11y-heading-background",
    });
    return;
  }

  if (style === "left-border") {
    applyNavigationEffectValue({
      attribute,
      document,
      value: "inset 4px 0 0 #f7c948",
      variable: "--migrosone-a11y-heading-box-shadow",
    });
    return;
  }

  applyNavigationEffectValue({
    attribute,
    document,
    value: "3px solid #f7c948",
    variable: "--migrosone-a11y-heading-outline",
  });
  applyNavigationEffectValue({
    attribute,
    document,
    value: "3px",
    variable: "--migrosone-a11y-heading-outline-offset",
  });
}

const definition = getFeaturePanelDefinition("highlight-headings");

if (!definition) {
  throw new Error("Missing highlight-headings feature definition");
}

export const highlightHeadingsFeatureModule: RuntimeFeatureModule = {
  key: "highlight-headings",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    const wrapper = document.createElement("label");
    const input = document.createElement("input");
    const text = document.createElement("span");

    wrapper.className = "migrosone-a11y-widget-feature-control";
    input.type = "checkbox";
    input.checked = resolveEnabled(config, preference);
    text.textContent = "Başlıkları vurgula";

    input.addEventListener("change", () => {
      onPreferenceChange(input.checked);
    });

    wrapper.append(input, text);
    return wrapper;
  },
  apply({ config, document, preference }) {
    resetHeadingVariables(document);
    if (!resolveEnabled(config, preference)) {
      return;
    }

    applyHeadingStyle(document, readStyle(config));
  },
  reset({ config, document }) {
    resetHeadingVariables(document);
    if (readBooleanDefault(config)) {
      applyHeadingStyle(document, readStyle(config));
    }
  },
};
